/* ==========================================================================
   Maštarije Nova — mst-ribbons.js
   <mst-ribbon-builder data-form-id> — per-piece list builder shared by the
   personalized-ribbons and name-tag products (snippets/mst-ribbon-builder.liquid).

   Two modes: "each" (row = colours + name, qty locked to 1) and "batch"
   (row = colours + qty + name). On submit every row becomes its own cart
   line via ONE POST to /cart/add.js with an items[] array + drawer sections.

   Colours arrive in one of two shapes, decided per product by the snippet:
     - as VARIANTS  (vrpce: colour is the variant, [data-rb-color] present);
     - as PROPERTIES ([data-rb-prop] selects, e.g. pločice, whose ribbon and
       foil colours are not variants — one variant, one price).
   Both end up on the cart line; only the variant id differs.

   All customer-facing wording (unit noun, labels, property keys, errors)
   comes from data-* attributes so the two products keep their own literal
   Croatian — labels double as line-item property keys.

   Progressive enhancement: on init it disables the no-JS fallback fields,
   reveals itself, hides the theme's variant picker / quantity / dynamic
   checkout for this section, and intercepts the product form's submit
   (listener on the form → runs before <mst-product-form>'s wrapper
   listener; stopImmediatePropagation keeps the single-variant path off).
   If this script never runs, the standard flow stays fully functional.

   patchSectionHTML is duplicated from mst-product.js on purpose — theme
   files stay self-contained (same convention as <mst-fc-slider>).
   ========================================================================== */

(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.MST && typeof window.MST.formatMoney === 'function') {
      return window.MST.formatMoney(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function capitalize(word) {
    return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
  }

  function patchSectionHTML(sectionHTML, sourceSelector, target) {
    if (!sectionHTML || !target) return;
    var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    var source = sourceSelector ? doc.querySelector(sourceSelector) : doc.body;
    if (source) target.innerHTML = source.innerHTML;
  }

  if (customElements.get('mst-ribbon-builder')) return;

  customElements.define(
    'mst-ribbon-builder',
    class MstRibbonBuilder extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId || '');
        this.rowsEl = this.querySelector('[data-rb-rows]');
        this.template = this.querySelector('[data-rb-template]');
        this.addBtn = this.querySelector('[data-rb-add]');
        this.addLabel = this.querySelector('[data-rb-add-label]');
        this.totalEl = this.querySelector('[data-rb-total]');
        this.errorEl = this.querySelector('[data-rb-error]');
        this.errorText = this.querySelector('[data-rb-error-text]');
        if (!this.form || !this.rowsEl || !this.template) return;

        try {
          var json = this.querySelector('[data-rb-variants]');
          this.variants = json ? JSON.parse(json.textContent) : [];
        } catch (err) {
          this.variants = [];
        }
        if (!this.variants.length) return;

        /* -- Per-product wording (see snippet) -------------------------- */
        var d = this.dataset;
        this.nameProp = d.nameProp || 'Ime na vrpci';
        this.unitOne = d.unitOne || 'vrpca';
        this.unitFew = d.unitFew || 'vrpce';
        this.unitMany = d.unitMany || 'vrpci';
        this.unitAcc = d.unitAcc || 'vrpcu';
        this.errorName = d.errorName || 'Upiši ime za svaku vrpcu.';
        this.errorQty = d.errorQty || 'Upiši količinu (1–500) za svaku boju.';

        this.mode = 'each';

        /* -- Take over from the standard single-variant UI -------------- */
        var section = this.closest('.shopify-section');
        if (section) section.classList.add('mst-section--ribbons');

        var pers = this.closest('mst-personalizer') || document;
        var fallback = pers.querySelector('[data-rb-fallback]');
        if (fallback) {
          fallback.hidden = true;
          fallback
            .querySelectorAll('input, textarea, select')
            .forEach(function (inp) {
              inp.disabled = true;
              /* the personalizer's capture validation checks every
                 [data-mst-required], hidden or not — take it out of play */
              inp.removeAttribute('data-mst-required');
              inp.removeAttribute('required');
            });
        }

        this.hidden = false;

        /* -- Wire up ---------------------------------------------------- */
        this.onModeChange = this.handleModeChange.bind(this);
        this.onAdd = this.handleAdd.bind(this);
        this.onRowsClick = this.handleRowsClick.bind(this);
        this.onRowsInput = this.handleRowsInput.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);

        this.querySelectorAll('[data-rb-mode]').forEach(
          function (radio) {
            radio.addEventListener('change', this.onModeChange);
          }.bind(this)
        );
        this.addBtn.addEventListener('click', this.onAdd);
        this.rowsEl.addEventListener('click', this.onRowsClick);
        this.rowsEl.addEventListener('input', this.onRowsInput);
        this.form.addEventListener('submit', this.onSubmit);

        if (!this.rowsEl.children.length) this.addRow(false);
        this.applyMode();
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      }

      /* Croatian plural: 1 vrpca, 2–4 vrpce, 5+ vrpci (11–14 → vrpci) */
      unitNoun(n) {
        var d = n % 10;
        var dd = n % 100;
        if (d === 1 && dd !== 11) return this.unitOne;
        if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return this.unitFew;
        return this.unitMany;
      }

      /* ---- rows ------------------------------------------------------- */
      addRow(focusName) {
        var rows = this.rows();
        var row = this.template.content.firstElementChild.cloneNode(true);
        var prev = rows[rows.length - 1];
        if (prev) {
          /* most common case: same colours again, another name */
          this.selectsOf(row).forEach(function (sel, index) {
            var prevSel = this.selectsOf(prev)[index];
            if (prevSel) sel.value = prevSel.value;
          }, this);
        }
        this.rowsEl.appendChild(row);
        this.renumber();
        this.updateSummary();
        if (focusName) row.querySelector('[data-rb-name]').focus();
      }

      rows() {
        return Array.prototype.slice.call(
          this.rowsEl.querySelectorAll('[data-rb-row]')
        );
      }

      /* Every colour select in a row, variant-backed or property-backed. */
      selectsOf(row) {
        return Array.prototype.slice.call(
          row.querySelectorAll('[data-rb-color], [data-rb-prop]')
        );
      }

      renumber() {
        var mode = this.mode;
        var unit = capitalize(this.unitOne);
        var self = this;
        this.rows().forEach(function (row, index) {
          var n = index + 1;
          row.querySelector('[data-rb-num]').textContent = n + '.';
          self.selectsOf(row).forEach(function (sel) {
            var what = sel.dataset.rbLabel || 'boja';
            sel.setAttribute('aria-label', unit + ' ' + n + ' — ' + what);
          });
          row
            .querySelector('[data-rb-name]')
            .setAttribute(
              'aria-label',
              unit + ' ' + n + ' — ' + self.nameProp.toLowerCase()
            );
          var qty = row.querySelector('[data-rb-qty]');
          qty.setAttribute(
            'aria-label',
            mode === 'batch' ? 'Stavka ' + n + ' — količina' : 'Količina'
          );
          row
            .querySelector('[data-rb-remove]')
            .setAttribute(
              'aria-label',
              mode === 'batch'
                ? 'Ukloni stavku ' + n
                : 'Ukloni ' + self.unitAcc + ' ' + n
            );
        });
      }

      handleRowsClick(event) {
        var removeBtn = event.target.closest('[data-rb-remove]');
        if (!removeBtn) return;
        if (this.rows().length <= 1) return;
        var row = removeBtn.closest('[data-rb-row]');
        var neighbour = row.nextElementSibling || row.previousElementSibling;
        row.remove();
        this.renumber();
        this.updateSummary();
        this.syncError();
        if (neighbour) neighbour.querySelector('[data-rb-name]').focus();
      }

      handleAdd() {
        this.addRow(true);
      }

      /* ---- modes ------------------------------------------------------ */
      handleModeChange(event) {
        this.mode = event.target.value === 'batch' ? 'batch' : 'each';
        this.applyMode();
      }

      applyMode() {
        this.classList.toggle('mst-rb--batch', this.mode === 'batch');
        if (this.addLabel) {
          this.addLabel.textContent =
            this.mode === 'batch'
              ? this.addLabel.dataset.labelBatch
              : this.addLabel.dataset.labelEach;
        }
        this.renumber();
        this.updateSummary();
      }

      /* ---- data ------------------------------------------------------- */
      rowData() {
        var mode = this.mode;
        var variantById = {};
        this.variants.forEach(function (v) {
          variantById[String(v.id)] = v;
        });
        var only = this.variants[0];
        return this.rows().map(function (row) {
          var qtyInput = row.querySelector('[data-rb-qty]');
          var qty = mode === 'batch' ? parseInt(qtyInput.value, 10) : 1;
          var colorSel = row.querySelector('[data-rb-color]');
          /* colour-as-property products have a single variant */
          var variant = colorSel ? variantById[colorSel.value] : only;

          var props = {};
          row.querySelectorAll('[data-rb-prop]').forEach(function (sel) {
            var key = sel.dataset.rbPropKey;
            if (key) props[key] = sel.value;
          });

          return {
            row: row,
            variant: variant,
            quantity: qty,
            qtyInput: qtyInput,
            nameInput: row.querySelector('[data-rb-name]'),
            name: row.querySelector('[data-rb-name]').value.trim(),
            props: props
          };
        });
      }

      updateSummary() {
        if (!this.totalEl) return;
        var count = 0;
        var cents = 0;
        this.rowData().forEach(function (entry) {
          var qty = entry.quantity;
          if (!qty || qty < 1 || isNaN(qty)) return;
          count += qty;
          if (entry.variant) cents += qty * entry.variant.price;
        });
        this.totalEl.textContent =
          'Ukupno: ' +
          count +
          ' ' +
          this.unitNoun(count) +
          ' · ' +
          formatMoney(cents);
      }

      /* ---- validation + submit ---------------------------------------- */
      showError(message, focusEl) {
        if (this.errorText) this.errorText.textContent = message;
        if (this.errorEl) this.errorEl.hidden = false;
        if (focusEl) {
          focusEl.focus();
          focusEl.scrollIntoView({
            behavior:
              window.matchMedia &&
              window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
            block: 'center'
          });
        }
      }

      /* ux-visual-21: a submit error used to stay up after the customer fixed
         the field. Clear each field as soon as it is valid again. */
      handleRowsInput(event) {
        this.updateSummary();
        var inp = event.target;
        if (!inp || !inp.classList || !inp.classList.contains('mst-invalid')) return;
        var valid;
        if (inp.matches('[data-rb-qty]')) {
          var qty = parseInt(inp.value, 10);
          valid = !isNaN(qty) && qty >= 1 && qty <= 500;
        } else {
          valid = inp.value.trim() !== '';
        }
        if (!valid) return;
        inp.classList.remove('mst-invalid');
        inp.setAttribute('aria-invalid', 'false');
        this.syncError();
      }

      /* Hide the message once no invalid field is left, otherwise make it
         describe the first one that still is. */
      syncError() {
        if (!this.errorEl || this.errorEl.hidden || !this.fieldError) return;
        var next = this.rowsEl.querySelector('.mst-invalid');
        if (!next) {
          this.errorEl.hidden = true;
        } else if (this.errorText) {
          this.errorText.textContent = next.matches('[data-rb-qty]')
            ? this.errorQty
            : this.errorName;
        }
      }

      clearError() {
        this.fieldError = false;
        if (this.errorEl) this.errorEl.hidden = true;
        this.rows().forEach(function (row) {
          row.querySelectorAll('.mst-invalid').forEach(function (inp) {
            inp.classList.remove('mst-invalid');
            inp.setAttribute('aria-invalid', 'false');
          });
        });
      }

      handleSubmit(event) {
        /* This product is ours: the single-variant path must never run. */
        event.preventDefault();
        event.stopImmediatePropagation();

        this.clearError();
        var entries = this.rowData();
        var firstInvalid = null;
        var errorMessage = '';
        var self = this;

        entries.forEach(function (entry) {
          var badQty =
            isNaN(entry.quantity) || entry.quantity < 1 || entry.quantity > 500;
          if (badQty) {
            entry.qtyInput.classList.add('mst-invalid');
            entry.qtyInput.setAttribute('aria-invalid', 'true');
            if (!firstInvalid) {
              firstInvalid = entry.qtyInput;
              errorMessage = self.errorQty;
            }
          }
          if (!entry.name) {
            entry.nameInput.classList.add('mst-invalid');
            entry.nameInput.setAttribute('aria-invalid', 'true');
            if (!firstInvalid) {
              firstInvalid = entry.nameInput;
              errorMessage = self.errorName;
            }
          }
        });

        if (firstInvalid) {
          this.fieldError = true;
          this.showError(errorMessage, firstInvalid);
          return;
        }

        /* merge rows that are identical in every respect (Shopify would
           merge those cart lines anyway — send them clean) */
        var merged = {};
        var order = [];
        entries.forEach(function (entry) {
          var properties = {};
          Object.keys(entry.props).forEach(function (key) {
            properties[key] = entry.props[key];
          });
          properties[self.nameProp] = entry.name;

          var key =
            entry.variant.id +
            '::' +
            Object.keys(properties)
              .map(function (k) {
                return k + '=' + properties[k];
              })
              .join('|');

          if (merged[key]) {
            merged[key].quantity += entry.quantity;
          } else {
            merged[key] = {
              id: entry.variant.id,
              quantity: entry.quantity,
              properties: properties
            };
            order.push(key);
          }
        });
        var items = order.map(function (key) {
          return merged[key];
        });

        var submitBtn = this.form.querySelector('button[type="submit"]');
        var formError = this.form.querySelector('[data-form-error]');
        if (
          submitBtn &&
          (submitBtn.disabled || submitBtn.getAttribute('aria-busy') === 'true')
        ) {
          return;
        }
        if (formError) {
          formError.hidden = true;
          formError.textContent = '';
        }
        if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

        var drawer = document.getElementById('MstCartDrawer');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: items,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            if (data.status && data.status !== 200) {
              throw new Error(data.description || data.message || '');
            }

            if (data.sections) {
              patchSectionHTML(
                data.sections['cart-drawer'],
                '[data-cart-drawer-inner]',
                document.querySelector('[data-cart-drawer-inner]')
              );
              patchSectionHTML(
                data.sections['cart-icon-bubble'],
                '.shopify-section',
                document.getElementById('cart-icon-bubble-wrap')
              );
            }

            return fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (response) {
                return response.json();
              })
              .then(function (cart) {
                var detail = { cart: cart };
                document.dispatchEvent(
                  new CustomEvent('mst:cart:updated', { detail: detail })
                );
                if (window.MST && window.MST.pubsub) {
                  window.MST.pubsub.publish('mst:cart:updated', detail);
                }
              })
              .catch(function () {
                document.dispatchEvent(
                  new CustomEvent('mst:cart:updated', { detail: { cart: null } })
                );
              })
              .then(function () {
                if (drawer && typeof drawer.open === 'function') {
                  drawer.open(submitBtn);
                } else if (drawer) {
                  drawer.setAttribute('open', '');
                } else {
                  window.location.href = '/cart';
                }
              });
          })
          .catch(function (error) {
            var message =
              (error && error.message) ||
              (formError && formError.dataset.genericError) ||
              '';
            if (formError) {
              formError.textContent = message;
              formError.hidden = false;
            } else {
              self.showError(message || 'Greška pri dodavanju u košaricu.');
            }
          })
          .finally(function () {
            if (submitBtn) submitBtn.removeAttribute('aria-busy');
          });
      }
    }
  );
})();
